import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Button, Grid, Tooltip } from "@material-ui/core";
import { GetApp, SupervisorAccount, Settings, MailOutline } from "@material-ui/icons";

import { rowRender } from "../utils/render";
import { KPTable } from "../../../components/KPTComponents";
import { STUDENT_RESEARCH } from "../../../constants/selectOptions";

import select from "../../../utils/select";
import ActionButton from "../utils/ActionButton";
import StudentProjectModal from "../StudentProjectModal";
import NoteMailSettingDialog from "../NoteMailSettingDialog";
import ROUTER from "../../../constants/router";
import { AcademicRankAbbrv, DegreeAbbrv } from "../../../utils/abbreviation";
import {
  STUDENT_RESEARCH_STATUS_LABEL,
  STUDENT_RESEARCH_STATUS,
  MAIN_PRIZE_OPTIONS,
  DURATION_OPTIONS,
} from "../../../constants/projectStatus";
// Refactor 2026-05-21: filter "Sắp đến hạn / Quá hạn" — chip bar +
// dropdown drill-down + cột "Sắp hạn" có badge + tooltip chi tiết.
import { DEADLINE_KINDS_SR } from "../../../constants/deadlineKinds";
import { computeDeadlines, rowMatchesFilter } from "../../../utils/deadlineCompute";
import useDueDate from "../../../utils/useDueDate";
import { DeadlineFilterBar, DeadlineBadge } from "../../../components/DeadlineFilter";

const sortFunc = (a, b) => {
  if (a.researchRole === "main") return -1;
  if (b.researchRole === "main") return 1;
  return a.id - b.id;
};

const filterLeavers = (members) => members?.filter((member) => member.leaveReason === "");

function StudentProjectTable(props) {
  const history = useHistory();
  const { type } = props;

  const [open, setOpen] = useState(null);
  const [noteMailOpen, setNoteMailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [datasource, setDatasource] = useState([]);

  const list = useSelector((state) => select(state, "studentResearchReducer", "list")) || [];
  const isFetching = useSelector((state) => select(state, "studentResearchReducer", "isFetching"));
  // Refactor 2026-06-02: chỉ sudo-admin + admin Phòng QLKH (type="admin") được
  // xoá đề tài. Inject "delete" vào actions sau rowRender để không phải sửa
  // từng row trong rules table.
  const userRole = useSelector((state) => select(state, "userReducer", "role"));
  const canDelete = userRole === "sudo-admin" || type === "admin";

  // Refactor 2026-05-21: filter sắp đến hạn / quá hạn.
  // chip: cấp độ urgency (overdue / soon7 / soon30 / all).
  // kindKey: drill-down theo loại deadline (endTime / bctd / acceptance / all).
  const [chip, setChip] = useState("all");
  const [kindKey, setKindKey] = useState("all");
  // reloadKey từ parent (bumped khi user click Replay) → useDueDate refetch
  // YearlyDueDate kèm theo list refetch để deadline cell luôn fresh.
  const { dueDate } = useDueDate("student", props.year, props.reloadKey);

  // Memoize handlers to avoid recreating on every render
  const handleOpenSetting = useCallback(() => {
    setOpen({ setting: true });
  }, []);

  const handleOpenExport = useCallback(() => {
    setOpen({
      export: true,
      rows: datasource,
    });
  }, [datasource]);

  const handleNavigateToCouncil = useCallback(() => {
    history.push(`${ROUTER.NCKH_SINH_VIEN}/council`);
  }, [history]);

  // Refactor 2026-05-21: layout giống IR — Mã đề tài đặt FIRST làm buffer
  // không-bleed giữa Tên đề tài (rộng nhất) và các cột GV/SV/Vai trò (bleed).
  // Trước đó GV với `width: calc(100% + 32px)` cell bleed sát Tên đề tài → khi
  // viewport hẹp + Tên đề tài shrink xuống ~280px, text dài đè lên cột GV.
  // 2026-07-31 (2): mã đề tài tự sinh dạng "SVNCKH_<khoa>_xx.yy" dài hơn hẳn mã
  // cũ, không có wordBreak nên tràn (bleed) sang cột Tên đề tài kế bên khi
  // table-layout:fixed. Thêm wordBreak + tăng nhẹ width 92→110 để mã wrap gọn
  // trong ô thay vì tràn ra ngoài.
  // Tổng width fixed = 928 (110+150+140+76+72+108+200+72).
  // Tên đề tài: max(420px, calc(100% - 928px)) — sàn 420 để text có chỗ hiển
  // thị rộng rãi, chấp nhận bảng scroll ngang nhiều hơn trên viewport hẹp.
  var columns = [
    {
      title: <b>Mã đề tài</b>,
      key: "researchCode",
      width: 110,
      searchable: true,
      render: (value) =>
        value ? (
          <span style={{ wordBreak: "break-word" }}>{value}</span>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        ),
    },
    {
      title: <b>Tên đề tài</b>,
      key: "name",
      width: "max(420px, calc(100% - 928px))",
      searchable: true,
      render: (value, row) => (
        <span style={{ wordBreak: "break-word" }}>
          {!row.status.find((e) => e.value === "report_submitted") ? value : row.parentResearch?.name}
        </span>
      ),
    },
    {
      title: <b>Giảng viên hướng dẫn</b>,
      key: "instructors",
      width: 150,
      render: (_, row) => {
        const value = !row.status.find((e) => e.value === "report_submitted")
          ? row.instructors
          : row.parentResearch?.instructors;
        return (
          <Box margin="-6px -16px" height={row.maxHeight} width="calc(100% + 32px)">
            {value?.sort(sortFunc)?.map((e, id) => (
              <Box
                key={`column-instructor-${id}`}
                height={`calc(${100 / value?.length}% - ${id === 0 ? 0 : 1}px)`}
                borderTop={id !== 0 ? "solid 1px #e0e0e0" : null}
                display="flex"
                alignItems="center"
                padding="0 16px"
                minHeight={32}
                fontWeight={e.researchRole === "main" ? "bold" : "auto"}
              >
                {`${AcademicRankAbbrv(e?.academicRank)}
                ${DegreeAbbrv(e?.degree)}
                ${e?.name}`}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      title: <b>Sinh viên</b>,
      key: "researchMembers",
      width: 140,
      render: (_, row) => {
        const members = filterLeavers(
          !row.status.find((e) => e.value === "report_submitted")
            ? row.researchMembers
            : row.parentResearch?.researchMembers
        );
        return (
          <Box margin="-6px -16px" height={row.maxHeight} width="calc(100% + 32px)">
            {members?.sort(sortFunc)?.map((e, id) => (
              <Box
                key={`column-member-${id}`}
                width="calc(100% - 32px)"
                height={`calc(${100 / members?.length}% - ${id === 0 ? 0 : 1}px)`}
                borderTop={id !== 0 ? "solid 1px #e0e0e0" : null}
                display="flex"
                alignItems="center"
                minHeight={id === 0 ? 32 : 31}
                padding="0 16px"
                fontWeight={e.researchRole === "main" ? "bold" : "auto"}
              >
                {`${e?.name}`}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      title: <b>Vai trò</b>,
      key: "researchMembersRole",
      width: 76,
      align: "right",
      render: (_, row) => {
        const members = filterLeavers(
          !row.status.find((e) => e.value === "report_submitted")
            ? row.researchMembers
            : row.parentResearch?.researchMembers
        );
        return (
          <Box margin="-6px -16px" width="calc(100% + 32px)" height={row.maxHeight}>
            {members?.sort(sortFunc)?.map((e, id) => (
              <Box
                key={`column-member-role-${id}`}
                width="calc(100% - 32px)"
                height={`calc(${100 / members?.length}% - ${id === 0 ? 0 : 1}px)`}
                borderTop={id !== 0 ? "solid 1px #e0e0e0" : null}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                minHeight={id === 0 ? 32 : 31}
                padding="0 16px"
                fontWeight={e.researchRole === "main" ? "bold" : "auto"}
              >
                {STUDENT_RESEARCH.researchRoleMemberOptions.find((role) => role.value === e.researchRole)?.title}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      title: <b>Thời gian</b>,
      key: "duration",
      width: 72,
      align: "center",
      render: (value) =>
        DURATION_OPTIONS.find((o) => o.value === value)?.label || "—",
    },
    {
      title: <b>Giải</b>,
      key: "mainPrize",
      width: 108,
      render: (value, row) => {
        const main = MAIN_PRIZE_OPTIONS.find((o) => o.value === value)?.label;
        const sideCount = row?.sidePrizes?.length || 0;
        if (!main && !sideCount) return <span style={{ color: "#aaa" }}>—</span>;
        return (
          <Box>
            {main && <div><b>{main}</b></div>}
            {sideCount > 0 && (
              <div style={{ fontSize: 12, color: "#777" }}>
                +{sideCount} giải phụ
              </div>
            )}
          </Box>
        );
      },
    },
    {
      title: <b>Trạng thái</b>,
      key: "status",
      width: 200,
      filterable: true,
      // Sau refactor de_tai_sv: dùng state machine mới từ projectStatus.js.
      // Generate filters tự động để không còn bị lệch giữa enum và UI.
      filters: STUDENT_RESEARCH_STATUS.map((value) => ({
        value,
        text: STUDENT_RESEARCH_STATUS_LABEL[value] || value,
      })),
      onFilter: (value, record) => {
        // value is the row/record, record is the filter object
        // Check if the row has any of the selected statuses
        if (!value.rawStatus) return false;
        return value.rawStatus.some((status) => record[status] === true);
      },
      render: (value, row) => (
        <>
          {value.map((e, index) => (
            <Box
              key={`status-${row.id}-${index}`}
              bgcolor={`${e.bgcolor}16`}
              border={`solid 1px ${e.bgcolor}`}
              color={e.bgcolor}
              fontSize={12}
              borderRadius={16}
              padding="2px 0"
              width="calc(100% - 32px)"
              margin="auto"
              textAlign="center"
              marginTop={index !== 0 ? 1 : 0}
            >
              {e.label}
            </Box>
          ))}
        </>
      ),
    },
    {
      // Refactor 2026-05-21: cột deadline. Hiển thị badge worst-case + tooltip
      // chi tiết khi hover. Cell tính `_deadlines` đã set ở datasource useMemo.
      title: <b>Sắp hạn</b>,
      key: "_deadlines",
      width: 110,
      align: "center",
      render: (value) => (
        <DeadlineBadge deadlines={value || []} kindKey={kindKey} />
      ),
    },
    {
      title: <b>Tác vụ</b>,
      key: "actions",
      width: 72,
      render: (value, row) => (
        <ActionButton
          row={row}
          actions={value}
          setOpen={setOpen}
          setDetail={setDetail}
          parent={list?.find((e) => row.parent === e.id)}
        />
      ),
    },
  ];

  useEffect(() => {
    const fullRows = list.map((e) => {
      // Parse status và tạo bản copy để tránh mutate dữ liệu gốc
      let statusArray;
      if (typeof e?.status === "string") {
        statusArray = e.status.split(",");
      } else if (Array.isArray(e?.status)) {
        statusArray = [...e.status]; // Create a shallow copy
      } else {
        statusArray = [];
      }

      return { ...e, status: statusArray };
    });
    const _datasource = fullRows.map((row) => {
      const { status, buttons } = rowRender(row.status, type, row);

      const instructorsLength = !status.find((e) => e.value === "report_submitted")
        ? row?.instructors?.length
        : row?.parentResearch?.instructors?.length;
      const researchMembersLength = !status.find((e) => e.value === "report_submitted")
        ? row?.researchMembers?.length
        : row?.parentResearch?.researchMembers?.length;
      const maxHeight = Math.max(instructorsLength, researchMembersLength) * 32;

      // Refactor 2026-05-21: compute deadlines cho row (dùng dueDate đã fetch).
      // Lưu vào field `_deadlines` để cell render + filter useMemo tái dùng.
      const _deadlines = computeDeadlines(
        { ...row, rawStatus: row.status, status },
        dueDate,
        DEADLINE_KINDS_SR
      );

      // Refactor 2026-06-02: append "delete" cho role được phép xoá.
      const finalButtons =
        canDelete && !buttons.includes("delete") ? [...buttons, "delete"] : buttons;

      return {
        ...row,
        rawStatus: row.status,
        status,
        actions: finalButtons,
        maxHeight: maxHeight,
        _deadlines,
      };
    });

    if (list) setDatasource(_datasource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, dueDate, canDelete]);

  // Refactor 2026-05-21: filter rows theo chip + kindKey + tính count cho mỗi chip.
  // Count tính theo kindKey hiện tại để user biết "chip ≤7 ngày của loại đang chọn".
  const filteredDatasource = useMemo(() => {
    if (chip === "all" && kindKey === "all") return datasource;
    return datasource.filter((row) =>
      rowMatchesFilter(row._deadlines || [], chip, kindKey)
    );
  }, [datasource, chip, kindKey]);

  const counts = useMemo(() => {
    const c = { all: datasource.length, overdue: 0, soon7: 0, soon30: 0 };
    for (const row of datasource) {
      const dl = row._deadlines || [];
      if (rowMatchesFilter(dl, "overdue", kindKey)) c.overdue++;
      if (rowMatchesFilter(dl, "soon7", kindKey)) c.soon7++;
      if (rowMatchesFilter(dl, "soon30", kindKey)) c.soon30++;
    }
    return c;
  }, [datasource, kindKey]);

  return (
    // Layout 2026-09-11: flex column cao 100% — DeadlineFilterBar giữ chiều cao
    // tự nhiên, KPTable (fillHeight) chiếm phần còn lại. Nhờ vậy paging luôn
    // nằm trong khung, không cần canh `calc(100vh - Npx)` nữa.
    <Grid
      item
      xs={12}
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box flexShrink={0}>
        <DeadlineFilterBar
          chip={chip}
          setChip={setChip}
          kindKey={kindKey}
          setKindKey={setKindKey}
          kinds={DEADLINE_KINDS_SR}
          counts={counts}
        />
      </Box>
      <KPTable
        fillHeight
        rows={filteredDatasource}
        columns={columns}
        border={true}
        // Refactor 2026-05-21: KPTable chỉ slice rows theo trang khi nhận
        // `localPaging={true}` (xem KPTable.js dòng 105). Trước đây bảng đẩy
        // toàn bộ list ra DOM, pagination bar chỉ trang trí — click "Trang 2"
        // không có tác dụng. Bật flag để pagination hoạt động đúng.
        localPaging={true}
        rowsPerPage={10}
        loading={isFetching}
        filterObj={props.filterObj}
        // Refactor 2026-05-21: bỏ `tableLayout: fixed` (cũ ép col co
        // proportional khi tổng > container → Tên đề tài shrink dưới sàn
        // `max(320px,…)`, text tràn ra che cột khác). Dùng default `auto` —
        // browser respect min/maxWidth strictly + TableContainer overflow-x
        // tự bật scroll ngang khi không đủ chỗ. Match pattern IR.
      />

      {/* Toolbar đáy bảng — nằm ở góc trái thanh pagination. Dùng Button outlined
          (có label) thay cho IconButton trần để rõ chức năng và đồng bộ kiểu
          dáng. "Check trùng đề tài" tạm ẩn — chưa có action/BE wire-up. */}
      {(props.type === "admin" || props.type === "mod" || props.type === "conference") && (
        <Box
          position="absolute"
          bottom={8}
          left={8}
          display="flex"
          alignItems="center"
          gridGap={8}
          zIndex={2}
        >
          {(props.type === "conference" || props.type === "admin") && (
            <Tooltip title="Xuất file báo cáo">
              <Button
                variant="outlined"
                size="small"
                startIcon={<GetApp />}
                onClick={handleOpenExport}
              >
                Xuất file
              </Button>
            </Tooltip>
          )}
          {props.type === "conference" && (
            <Tooltip title="Hội đồng các tiểu ban">
              <Button
                variant="outlined"
                size="small"
                startIcon={<SupervisorAccount />}
                onClick={handleNavigateToCouncil}
              >
                Hội đồng
              </Button>
            </Tooltip>
          )}
          {(props.type === "admin" || props.type === "conference") && (
            <Tooltip title="Cấu hình deadline & hội nghị">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Settings />}
                onClick={handleOpenSetting}
              >
                Cấu hình
              </Button>
            </Tooltip>
          )}
          {props.type === "admin" && (
            <Tooltip title="Bật/tắt & chỉnh mẫu email gửi ghi chú kết quả các cấp cho GV hướng dẫn">
              <Button
                variant="outlined"
                size="small"
                startIcon={<MailOutline />}
                onClick={() => setNoteMailOpen(true)}
              >
                Email ghi chú
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      {props.type === "admin" && (
        <NoteMailSettingDialog open={noteMailOpen} onClose={() => setNoteMailOpen(false)} />
      )}

      <StudentProjectModal
        rows={list}
        open={open}
        detail={detail}
        type={props.type}
        year={props.year}
        chosenDepartmentId={props.chosenDepartmentId}
        setOpen={setOpen}
      />
    </Grid>
  );
}

export default StudentProjectTable;

